// The copyright rule for showing an article's abstract / full content. Elsevier
// works under a non-commercial or closed license are gated (their abstract is
// hidden). Publisher is inferred from the DOI prefix — there is no stored
// publisher field.
//
// `canDisplayArticleContent` is the only export, and it is the rule. The two
// halves below stay private: the article page used to re-assemble them inline
// while this function went uncalled, and the copy drifted exactly where it
// hurts — it fed the rule an `isPublic` that could be `undefined`, which reads
// as "private room" and lifts the gate for a visitor with no room at all.

// DOI registrant prefixes that resolve to Elsevier.
const doiPrefixToPublisherName: Record<string, string> = {
  "10.1016": "Elsevier",
  "10.1006": "Elsevier",
  "10.1367": "Elsevier",
  "10.1602": "Elsevier",
  "10.2353": "Elsevier",
  "10.1529": "Elsevier",
  "10.3816": "Elsevier",
  "10.3921": "Elsevier",
  "10.1157": "Elsevier",
  "10.1205": "Elsevier",
  "10.3182": "Elsevier",
  "10.4065": "Elsevier",
  "10.1383": "Elsevier",
  "10.1067": "Elsevier",
  "10.1078": "Elsevier",
  "10.1053": "Elsevier",
  "10.1580": "Elsevier",
  "10.1054": "Elsevier",
  "10.1197": "Elsevier",
  "10.1240": "Elsevier",
};

// Accepts a bare DOI or a doi.org URL (the citation sheet passes `doiLink`).
function isElsevierDoiPrefix(doi: string | null): boolean {
  if (doi === null) return false;
  const normalizedDoi = doi.replace(/^https?:\/\/(dx\.)?doi\.org\//, "");
  const prefix = normalizedDoi.split("/").at(0);
  if (prefix === undefined) return false;
  return doiPrefixToPublisherName[prefix] === "Elsevier";
}

// A missing/unknown license (`null`) counts as non-commercial — the conservative
// default (gate rather than expose).
function isNonCommercialLicense(license: string | null): boolean {
  switch (license) {
    case "cc-by-nc":
    case "cc-by-nc-nd":
    case "cc-by-nc-sa":
    case "elsevier-tdm":
    case "other-oa":
    case null: {
      return true;
    }
    default: {
      return false;
    }
  }
}

/**
 * Whether an article's abstract / full content may be displayed. Content is
 * hidden ONLY for an Elsevier work (by DOI prefix) that is closed-access or
 * carries a non-commercial license, on a public surface — unless the
 * `WEBAPP_SHOW_NC_LICENSE` override is on. `isPublic` gates the rule to public
 * surfaces (a private/B2B room shows everything).
 */
export function canDisplayArticleContent(params: {
  doi: string | null;
  license: string | null;
  isOpenAccess: boolean | null;
  isPublic: boolean;
  showNcLicense: boolean;
}): boolean {
  return (
    params.showNcLicense ||
    !params.isPublic ||
    !isElsevierDoiPrefix(params.doi) ||
    (params.isOpenAccess === true && !isNonCommercialLicense(params.license))
  );
}
